import { useEffect, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "@/lib/api";

export default function Captcha({ onChange }) {
  const [mode, setMode] = useState(null);
  const [siteKey, setSiteKey] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState("");
  const widgetRef = useRef(null);
  const rendered = useRef(false);

  const loadBuiltin = async () => {
    const { data } = await api.get("/captcha");
    setChallenge(data);
    setAnswer("");
    onChange?.({ token: data.token, answer: "" });
  };

  useEffect(() => {
    api
      .get("/settings/public-config")
      .then(({ data }) => {
        if (data.turnstile_site_key) {
          setMode("turnstile");
          setSiteKey(data.turnstile_site_key);
        } else {
          setMode("builtin");
          loadBuiltin();
        }
      })
      .catch(() => {
        setMode("builtin");
        loadBuiltin();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode !== "turnstile" || !siteKey) return;
    const render = () => {
      if (!window.turnstile || !widgetRef.current || rendered.current) return;
      rendered.current = true;
      window.turnstile.render(widgetRef.current, {
        sitekey: siteKey,
        callback: (t) => onChange?.({ token: t, answer: null }),
        "expired-callback": () => onChange?.({ token: "", answer: null }),
        "error-callback": () => onChange?.({ token: "", answer: null }),
      });
    };
    if (window.turnstile) {
      render();
    } else {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = render;
      document.head.appendChild(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, siteKey]);

  const labelCls = "mb-2 block text-[10px] font-semibold uppercase tracking-widest text-[color:var(--ink-muted)]";
  const inputCls =
    "w-full rounded-md border border-[color:var(--ink-hairline)] bg-white px-4 py-3 text-sm text-[color:var(--ink)] focus:border-[color:var(--evergreen)] focus:outline-none focus:ring-2 focus:ring-[color:var(--evergreen-soft)]";

  if (mode === "turnstile") {
    return (
      <div>
        <label className={labelCls}>Verify you're human</label>
        <div ref={widgetRef} data-testid="captcha-turnstile" />
      </div>
    );
  }

  if (!challenge) return null;

  return (
    <div data-testid="captcha-builtin">
      <label className={labelCls}>Human check — what is {challenge.question}?</label>
      <div className="flex gap-2">
        <input
          value={answer}
          inputMode="numeric"
          data-testid="captcha-answer"
          placeholder="Type the answer"
          onChange={(e) => {
            setAnswer(e.target.value);
            onChange?.({ token: challenge.token, answer: e.target.value });
          }}
          className={inputCls}
        />
        <button
          type="button"
          onClick={loadBuiltin}
          data-testid="captcha-refresh"
          className="btn-edge-ghost text-xs"
          aria-label="New challenge"
        >
          <RefreshCw size={14} />
        </button>
      </div>
    </div>
  );
}
